import { LEGACY_ROLES } from 'components/account/AccountView/users/constants';
import { SHOPPER_CAMPAIGNS_SDPOC } from 'components/admin/utils/feature-flags';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import useAuth from 'hooks/useAuth';

/**
 * Whether the in-progress Shopper Campaigns (SDPOC) feature is available to the
 * current user: either a shop with the SDPOC flag enabled, or an internal AI
 * alpha tester (regardless of the shop's flag).
 *
 * Gate: `shopper_campaigns_sdpoc` flag OR the AI alpha-tester role. Flagging a
 * shop exposes the feature to that shop's users, and internal AI alpha testers
 * can always see it (e.g. for review/support) even on unflagged shops.
 *
 * Postscript admin alone is deliberately NOT sufficient: CSMs are admins but
 * are not alpha testers, so gating on the role keeps this in-progress surface
 * hidden from them while the internal team can still see it.
 *
 * Note: this controls UI *visibility* only. The Flow Builder "Convert to
 * Shopper campaign" action stays stricter — flag AND alpha tester — via
 * `useCanConvertFlowToShopperCampaign`, mirroring the backend `/from-flow`
 * endpoint's `AI_ALPHA_TESTER` requirement (see marketing_ai/shopper_campaigns).
 */
export const useShowSdpocCampaigns = (): boolean => {
  const { hasLabsFlag } = usePSLabs();
  const { userHasLegacyRole } = useAuth();

  return (
    hasLabsFlag(SHOPPER_CAMPAIGNS_SDPOC) ||
    userHasLegacyRole(LEGACY_ROLES.AI_ALPHA_TESTER)
  );
};
